import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import * as R from 'ramda'

import useCurrentUser from 'hooks/use-current-user'
import useInterval from 'hooks/use-interval'
import { recordCoachWorkHeartbeat as recordCoachWorkHeartbeatMutation } from './mutations.graphql'
import { myCoachWorkTime as myCoachWorkTimeQuery } from './queries.graphql'
import {
  HEARTBEAT_INTERVAL_MS,
  INTERACTION_THROTTLE_MS,
  TICK_MS,
  ACTIVITY_EVENTS,
  isActive,
  isCrediting,
  nextCreditState,
  isOverWarnThreshold,
} from './logic'

interface CoachWorkTimeContextValue {
  // W2 coaches get the front-end treatment; everyone is still tracked.
  isW2: boolean
  // Whether time is accruing right now (drives the live status dot): the coach
  // is locally active AND the server is still crediting (grey during a call).
  isCounting: boolean
  // Server-authoritative week-to-date seconds (updated on each heartbeat ack
  // and on query refetch). The UI shows minute granularity; the server is the
  // clock — we never tick this locally.
  displaySeconds: number
  // Weekly non-call budget (20% of last week's call time; 0 if none).
  weeklyBudgetSeconds: number
  // True once tracked time reaches 80% of the weekly budget.
  isOverBudgetWarning: boolean
}

const DEFAULT_VALUE: CoachWorkTimeContextValue = {
  isW2: false,
  isCounting: false,
  displaySeconds: 0,
  weeklyBudgetSeconds: 0,
  isOverBudgetWarning: false,
}

const CoachWorkTimeContext =
  createContext<CoachWorkTimeContextValue>(DEFAULT_VALUE)

export const useCoachWorkTime = () => useContext(CoachWorkTimeContext)

export const CoachWorkTimeProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const router = useRouter()
  const currentUser = useCurrentUser()
  const coach = currentUser?.data?.me?.user?.coach
  const coachId = coach?.id
  const isW2 = coach?.employmentType === 'w2'

  // Track for every coach on the dashboard; the UI is gated on isW2 elsewhere.
  const onDashboard = router.pathname.startsWith('/dashboard')
  const enabled = !!coachId && onDashboard

  // CLIENT context when on a client-scoped page, else OTHER.
  const clientId = (router.query.clientId as string) || null

  const [recordHeartbeat] = useMutation(recordCoachWorkHeartbeatMutation)

  // Week-to-date total + weekly budget, fetched for W2 coaches only (the live
  // timer seed and the over-budget warning). Non-W2 coaches show no UI, so skip.
  const { data: workTimeData } = useQuery(myCoachWorkTimeQuery, {
    skip: !enabled || !isW2,
    fetchPolicy: 'cache-and-network',
  })
  const weeklyBudgetSeconds =
    workTimeData?.myCoachWorkTime?.weeklyBudgetSeconds ?? 0

  const [isCounting, setIsCounting] = useState(false)
  const [displaySeconds, setDisplaySeconds] = useState(0)

  // Refs so the interval/event callbacks always read fresh values without
  // resubscribing.
  const lastInteractionRef = useRef(Date.now())
  const lastThrottleRef = useRef(0)
  const isVisibleRef = useRef(true)
  const clientIdRef = useRef(clientId)
  const enabledRef = useRef(enabled)
  clientIdRef.current = clientId
  enabledRef.current = enabled
  // High-water mark of the server week-total we've seen, and when a *credited*
  // heartbeat last raised it. Drives the dot (see isCreditingNow). Initialised
  // to 0 so the dot stays grey until a real credited beat — no green flash on
  // mount, and a seed/refetch that raises the total doesn't green it either.
  const lastServerSecondsRef = useRef(0)
  const lastCreditedAtRef = useRef(0)

  // Locally active = visible tab + a real action within the idle window. Drives
  // whether we *send* beats; the dot additionally requires the server to be
  // crediting (below).
  const isLocallyActive = () =>
    isActive({
      visible: isVisibleRef.current,
      lastInteractionAt: lastInteractionRef.current,
      now: Date.now(),
    })

  const isCreditingNow = () =>
    isCrediting({ creditedAt: lastCreditedAtRef.current, now: Date.now() })

  // The readout is server-authoritative: show the latest week total from the
  // server (heartbeat ack or query refetch) — never a locally-ticked estimate,
  // so it can't drift ahead or jump backward. Only a heartbeat *ack* that
  // raises the total (`fromCredit`) advances the "credited" marker that greens
  // the dot; a seed/refetch can raise the total without the server crediting
  // right now (e.g. returning to a tab during a call), so it updates the
  // readout only.
  const applyServerSeconds = useCallback(
    (seconds: number, fromCredit = false) => {
      const next = nextCreditState({
        prevSeenSeconds: lastServerSecondsRef.current,
        prevCreditedAt: lastCreditedAtRef.current,
        seconds,
        fromCredit,
        now: Date.now(),
      })
      lastServerSecondsRef.current = next.seenSeconds
      lastCreditedAtRef.current = next.creditedAt
      setDisplaySeconds(seconds)
    },
    []
  )

  // Seed / refresh from the server's week-to-date (also refetched on tab
  // refocus, so a coach returning to an idle tab sees the correct total).
  useEffect(() => {
    const tracked = workTimeData?.myCoachWorkTime?.trackedSeconds
    if (typeof tracked === 'number') applyServerSeconds(tracked)
  }, [workTimeData, applyServerSeconds])

  const fireHeartbeat = () => {
    if (!enabledRef.current || !isLocallyActive()) return
    const cid = clientIdRef.current
    recordHeartbeat({
      variables: {
        contextType: cid ? 'CLIENT' : 'OTHER',
        clientId: cid,
      },
    })
      .then(res => {
        // The server is the clock: snap the readout to its authoritative week
        // total.
        const weekSeconds = R.path(
          ['data', 'recordCoachWorkHeartbeat', 'weekTrackedSeconds'],
          res
        )
        if (typeof weekSeconds === 'number')
          applyServerSeconds(weekSeconds, true)
      })
      .catch(() => {
        // A dropped heartbeat is non-fatal: it just misses one ~25s slice, and
        // the next beat resumes crediting.
      })
  }

  // Activity + visibility listeners (client-only, while enabled).
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    const onInteraction = () => {
      const now = Date.now()
      if (now - lastThrottleRef.current >= INTERACTION_THROTTLE_MS) {
        lastThrottleRef.current = now
        lastInteractionRef.current = now
      }
    }
    const onVisibility = () => {
      // Visibility only gates counting; coming back into view is not itself a
      // work action, so we don't reset the activity timer here.
      isVisibleRef.current = document.visibilityState === 'visible'
    }

    const opts: AddEventListenerOptions = { passive: true, capture: true }
    ACTIVITY_EVENTS.forEach(evt =>
      window.addEventListener(evt, onInteraction, opts)
    )
    document.addEventListener('visibilitychange', onVisibility)

    isVisibleRef.current = document.visibilityState === 'visible'
    // Opening the dashboard is itself an action (navigation) → start a window.
    lastInteractionRef.current = Date.now()

    return () => {
      ACTIVITY_EVENTS.forEach(evt =>
        window.removeEventListener(evt, onInteraction, opts)
      )
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled])

  // Recompute the dot: green only while locally active AND the server is still
  // crediting. Handles the idle timeout, tab hide, and live-call greying with no
  // explicit event. The readout itself is server-driven, so it's untouched here.
  useInterval(
    () => {
      const counting = isLocallyActive() && isCreditingNow()
      setIsCounting(prev => (prev === counting ? prev : counting))
    },
    enabled ? TICK_MS : null
  )

  // Heartbeat cadence.
  useInterval(fireHeartbeat, enabled ? HEARTBEAT_INTERVAL_MS : null)

  // Immediate heartbeat on enable and on context (client) switch, so the switch
  // is attributed to the new client right away rather than on the next ~25s tick.
  useEffect(() => {
    if (!enabled) {
      setIsCounting(false)
      return
    }
    fireHeartbeat()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, clientId])

  return (
    <CoachWorkTimeContext.Provider
      value={{
        isW2,
        isCounting,
        displaySeconds,
        weeklyBudgetSeconds,
        isOverBudgetWarning: isOverWarnThreshold({
          displaySeconds,
          weeklyBudgetSeconds,
        }),
      }}>
      {children}
    </CoachWorkTimeContext.Provider>
  )
}

export default CoachWorkTimeProvider
